import { useMemo } from "react";
import PennText from "@/components/ui/text/PennText";
import { Skeleton } from "@/components/ui";
import { ProductHoverCard } from "./ProductHoverCard";
import type { SelectorProduct } from "@/api/filter";
import { PAGE_CONTENT_CLASS } from "@/constants/pageContent";

type SolutionGridProps = {
  products: SelectorProduct[];
  selected: string[];
  onToggle: (id: string) => void;
  isPending?: boolean;
  isError?: boolean;
  errorMessage?: string | null;
};

type Group = { subcategory: string; items: SelectorProduct[] };
type Column = { category: string; count: number; groups: Group[] };

// POC parity — product grid ordering (#6).
// • categoryOrder (L3050): fixed column order.
// • subcategoryOrder (L10340): fixed sub-section order per category.
// • score() (L4763): products inside a sub-section sort by score DESC.
// Categories/subcategories not listed keep their API (insertion) order; POC
// drops unknown categories entirely, but we append them so nothing disappears.
const CATEGORY_ORDER = [
  "Handtools",
  "Standalone Machines",
  "Robotic Integration",
  "InDie",
  "Custom",
];

const SUBCATEGORY_ORDER: Record<string, string[]> = {
  Handtools: [
    "Electric Handtools",
    "Pneumo Hydraulic Handtools",
    "Pneumohydraulic Handtools",
    "Pneumatic Handtools",
    "Mechanical Handtools",
  ],
  "Standalone Machines": [
    "Servo-Electric",
    "Servoelectric",
    "Hydraulic",
    "Air-over-Oil",
    "Pneumatic",
  ],
};

/** POC normKey (L4944): lower-case + strip non-alphanumerics. */
const normKey = (s: string): string =>
  String(s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");

/** POC score (L4763): supports×10 + features×5 + maxSize. */
const productScore = (p: SelectorProduct): number => {
  const supports = [
    p.supportsPemClinch,
    p.supportsBlindRivet,
    p.supportsProfile,
    p.supportsOther,
  ].filter(Boolean).length;
  const features = [
    p.featuresProcessMonitoring,
    p.featuresRobotIntegration,
  ].filter(Boolean).length;
  const size = typeof p.maxSize === "number" ? p.maxSize : 0;
  return supports * 10 + features * 5 + size;
};

/** Order subcategory keys per POC orderedSubcategoryKeys (L10357). */
function orderSubcategories(category: string, keys: string[]): string[] {
  const order = SUBCATEGORY_ORDER[category];
  if (!order) return keys; // keep insertion order (POC parity)
  const idx = new Map<string, number>();
  order.forEach((k, i) => {
    const nk = normKey(k);
    if (!idx.has(nk)) idx.set(nk, i);
  });
  return [...keys].sort((a, b) => {
    const ia = idx.get(normKey(a)) ?? 999;
    const ib = idx.get(normKey(b)) ?? 999;
    if (ia !== ib) return ia - ib;
    return String(a).localeCompare(String(b));
  });
}

/** Order category keys per POC renderSelection (L10425). */
function orderCategories(present: string[]): string[] {
  // POC always renders the full fixed category set (its catalog contains every
  // category), showing a "0 matching" / empty column for categories with no
  // products after filtering. Mirror that here so 0-result categories like
  // "Handtools" stay visible instead of disappearing. Unknown categories that
  // turn up in the results are appended so nothing is silently hidden.
  const unknown = present
    .filter((c) => !CATEGORY_ORDER.includes(c))
    .sort((a, b) => a.localeCompare(b));
  return [...CATEGORY_ORDER, ...unknown];
}

export const SolutionGrid = ({
  products,
  selected,
  onToggle,
  isPending = false,
  isError = false,
  errorMessage = null,
}: SolutionGridProps) => {
  const columns = useMemo<Column[]>(() => {
    const byCategory = new Map<string, Map<string, SelectorProduct[]>>();
    for (const p of products) {
      const subMap =
        byCategory.get(p.category) ?? new Map<string, SelectorProduct[]>();
      const items = subMap.get(p.subcategory) ?? [];
      items.push(p);
      subMap.set(p.subcategory, items);
      byCategory.set(p.category, subMap);
    }

    return orderCategories(Array.from(byCategory.keys())).map((category) => {
      const subMap = byCategory.get(category) ?? new Map();
      const groups: Group[] = orderSubcategories(
        category,
        Array.from(subMap.keys()),
      ).map((subcategory) => ({
        subcategory,
        // Stable sort keeps API order for score ties (POC parity).
        items: [...(subMap.get(subcategory) ?? [])].sort(
          (a, b) => productScore(b) - productScore(a),
        ),
      }));
      const count = groups.reduce((acc, g) => acc + g.items.length, 0);
      return { category, count, groups };
    });
  }, [products]);

  return (
    <div>
      <div className={`${PAGE_CONTENT_CLASS} py-6`}>
        <PennText
          as="h2"
          size="2xl"
          color="on-navy"
          font="semibold"
          align="center"
        >
          Select solutions you want to compare
        </PennText>
        <PennText
          as="p"
          size="base"
          color="on-navy-secondary"
          align="center"
          className="mt-1 mb-5"
        >
          Choose one or more solutions, then click "Compare / Configure
          Solutions".
        </PennText>

        {isError ? (
          <PennText
            as="p"
            size="sm"
            color="error"
            align="center"
            className="py-6"
          >
            {errorMessage ?? "Failed to load products."}
          </PennText>
        ) : isPending && products.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <SolutionTileSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {columns.map((col) => (
              <div
                key={col.category}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-soft"
              >
                <div className="mb-3">
                  <PennText as="h3" size="lg" color="blue_grey1" font="semibold">
                    {col.category}
                  </PennText>
                  <PennText as="p" size="xs" color="blue_grey">
                    {col.count} matching
                  </PennText>
                </div>

                {col.groups.length === 0 && (
                  <PennText as="p" size="xs" color="blue_grey" className="mt-1">
                    No matching products for the current selection.
                  </PennText>
                )}

                {col.groups.map((grp) => (
                  <div key={grp.subcategory} className="mb-3">
                    <PennText
                      as="p"
                      size="xs"
                      color="blue_grey2"
                      font="medium"
                      className="uppercase mb-2"
                    >
                      {grp.subcategory}
                    </PennText>
                    <div className="space-y-1">
                      {grp.items.map((item) => (
                        <div
                          key={item.productId}
                          className="flex items-center gap-2"
                        >
                          <input
                            id={`solution-${item.productId}`}
                            type="checkbox"
                            checked={selected.includes(item.productId)}
                            onChange={() => onToggle(item.productId)}
                            className="w-3.5 h-3.5 accent-penn-500 cursor-pointer shrink-0"
                          />
                          <ProductHoverCard product={item}>
                            <label
                              htmlFor={`solution-${item.productId}`}
                              tabIndex={0}
                              className="cursor-pointer text-sm font-normal text-blue_grey hover:text-penn-900 transition-colors leading-snug"
                            >
                              {item.name}
                            </label>
                          </ProductHoverCard>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** Shimmer placeholder for a category column while products load. */
const SolutionTileSkeleton = () => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-soft">
    <Skeleton width="55%" height={16} className="mb-2" />
    <Skeleton width="30%" height={10} className="mb-4" />
    {[0, 1].map((g) => (
      <div key={g} className="mb-4">
        <Skeleton width="45%" height={9} className="mb-2" />
        <div className="space-y-2">
          {[0, 1, 2].map((r) => (
            <div key={r} className="flex items-center gap-2">
              <Skeleton width={14} height={14} rounded="sm" />
              <Skeleton width={`${70 - r * 8}%`} height={12} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
